import {
  ArrowRightIcon,
  ChatCircleDotsIcon,
  ChatCircleTextIcon,
  CheckIcon,
  HandHeartIcon,
  HandshakeIcon,
  LightbulbIcon,
  MagnifyingGlassIcon,
  MapTrifoldIcon,
  ScalesIcon,
  ShieldCheckIcon,
  TagIcon,
  UsersThreeIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type { TFunction } from 'i18next';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { FALLBACK_LIMITS, type PublicConfig, useConfig } from '@entities/config/api';
import { useMe } from '@entities/user/api';
import { HERO_PROMPT_STORAGE_KEY, ROUTES } from '@shared/config/routes';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Button, ButtonLink, Card, Container } from '@shared/ui';
import section from '@shared/ui/PublicSection/publicSection.module.css';
import { Reveal } from '@shared/ui/Reveal/Reveal';

import { EDITORIAL_PHOTOS, HERO_BACKDROP_WIDE_MEDIA } from './editorialPhoto';
import styles from './HomePage.module.css';

const EXAMPLE_KEYS = ['family', 'city', 'touring'] as const;

/**
 * Три столпа под заголовком hero (DESIGN_SYSTEM §7.1).
 *
 * ВНИМАНИЕ: «Prüfen» и «Bessere Konditionen» — прямое решение заказчика
 * (26.10.2026), осознанное отступление от CLAUDE.md §2: CarViQ.DE не
 * проверяет автомобиль — он спрашивает продавца — и не обещает результата
 * торга. То же относится к «Angebote zu prüfen» в подзаголовке. Формулировки
 * оставлены по решению заказчика и на другие экраны не распространяются
 * (DESIGN_SYSTEM §7.1, §8). **Не «чинить» молча** — сначала уточнить у заказчика.
 */
const PILLARS = [
  { key: 'find', icon: MagnifyingGlassIcon },
  { key: 'check', icon: ShieldCheckIcon },
  { key: 'negotiate', icon: HandshakeIcon },
] as const;

/** Нижняя строка под hero: три пункта с иконками, без подложек. */
const ASSURANCES = [
  { key: 'independent', icon: UsersThreeIcon },
  { key: 'trust', icon: HandHeartIcon },
  { key: 'germany', icon: MapTrifoldIcon },
] as const;

/**
 * Три этапа всего пути клиента — от запроса до подготовки к покупке (тексты
 * заказчика от 29.10.2026). Две колонки на строку, а не три равные карточки
 * (DESIGN_SYSTEM §7, §10).
 */
const STEPS = [
  { key: 'describe', icon: ChatCircleTextIcon, details: ['noModel', 'context', 'confirm'] },
  { key: 'compare', icon: ScalesIcon, details: ['photos', 'availability', 'separate'] },
  { key: 'deal', icon: HandshakeIcon, details: ['questions', 'extraChecks', 'help'] },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

interface StepPrice {
  lines: string[];
  note: string | null;
}

/**
 * Стоимость этапа — у самого этапа, чтобы 19,90 € не читались как цена всей
 * работы до покупки: Top-100 бесплатно, Top-4 — разовый платёж, сопровождение —
 * свои условия, и рядом с ними условия вознаграждения (DESIGN_SYSTEM §8).
 *
 * Суммы — только из `GET /config`. Главная обязана работать и без API: тогда
 * этап называет вид платежа без суммы, а не правдоподобную цифру.
 */
function stepPrice(
  key: StepKey,
  config: PublicConfig | undefined,
  locale: string,
  t: TFunction,
): StepPrice {
  if (key === 'describe') return { lines: [t('home.steps.describe.price')], note: null };

  if (key === 'compare') {
    const top4 = config
      ? t('home.steps.compare.price.top4', {
          price: money(config.payments.price_cents, config.payments.currency, locale),
        })
      : t('home.steps.compare.price.top4NoAmount');
    return { lines: [t('home.steps.compare.price.free'), top4], note: null };
  }

  const fee = config?.success_fee.enabled
    ? t('pricing.successFee', {
        rate: percentFromBps(config.success_fee.rate_bps, locale),
        flat: money(config.success_fee.flat_cents, config.payments.currency, locale),
      })
    : null;
  return { lines: [t('home.steps.deal.price')], note: fee };
}

/**
 * Главная страница (редизайн 2026-10, DESIGN_SYSTEM §7.1, макет
 * `screen_samples/2026-10/01-home.jpg`).
 *
 * Состав: hero с Karl → «Von der ersten Anfrage bis zur Kaufbegleitung»
 * (`#how-it-works`) → футер (его рисует `PublicLayout`). Блоки «Mission /
 * Werte», «CarViQ.DE in Zahlen» и тёмная CTA-полоса удалены вместе с
 * выдуманными числами и подписью «Gründer», которые в них жили.
 *
 * Karl — **сгенерированный образ AI-помощника**, а не фотография сотрудника
 * (DESIGN_SYSTEM §5.4). Поэтому у фигуры всегда стоит плашка «KI-Assistent»,
 * а alt называет изображение сгенерированным.
 *
 * Красная кнопка на экране одна — «Mit Karl starten» (§5.1). Мост в
 * приложение для вошедшего пользователя — `secondary`.
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const { data: session } = useMe();
  const limits = config?.limits ?? FALLBACK_LIMITS;
  const locale = i18n.resolvedLanguage ?? 'de';

  const [prompt, setPrompt] = useState('');
  const [touched, setTouched] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = prompt.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < limits.hero_prompt_min_length;
  const canSubmit = trimmed.length >= limits.hero_prompt_min_length;
  const showError = touched && tooShort;

  const submit = () => {
    setTouched(true);
    if (!canSubmit) return;
    // Запрос переживает авторизацию: список `/app` создаст под него новый
    // запрос и откроет диалог с этим текстом первым сообщением (AC-01).
    sessionStorage.setItem(HERO_PROMPT_STORAGE_KEY, trimmed);
    void navigate(ROUTES.app);
  };

  return (
    <>
      {/*
        1. Hero. Одна сетка на все ширины, меняется только расстановка:
        ≥ 768 — фото салона за всем hero, текст и запрос слева, Karl справа;
        < 768 — фото только под заголовком и Karl, столпы и запрос ниже на белом.
      */}
      <section className={styles.hero} aria-labelledby="hero-title">
        <Container className={styles.heroInner}>
          {/*
            Фото салона — декоративный фон (alt=""). `<picture>` отдаёт телефону
            вертикальный кадр, а широкий не качает вовсе.
          */}
          <picture className={styles.backdrop}>
            <source
              media={HERO_BACKDROP_WIDE_MEDIA}
              srcSet={EDITORIAL_PHOTOS.showroomWide.src}
              width={EDITORIAL_PHOTOS.showroomWide.width}
              height={EDITORIAL_PHOTOS.showroomWide.height}
            />
            <img
              className={styles.backdropImage}
              src={EDITORIAL_PHOTOS.showroomTall.src}
              width={EDITORIAL_PHOTOS.showroomTall.width}
              height={EDITORIAL_PHOTOS.showroomTall.height}
              alt=""
              decoding="async"
            />
          </picture>

          <div className={styles.heroCopy}>
            {/* Акцент в заголовке — DESIGN_SYSTEM §3.3: один цветной фрагмент. */}
            <h1 className={styles.title} id="hero-title">
              <span>
                {t('home.hero.titleStart')}{' '}
                <em className={styles.titleAccent}>{t('home.hero.titleName')}</em>
                {t('home.hero.titleEnd')}
              </span>{' '}
              <span className={styles.titleSecond}>{t('home.hero.titleRole')}</span>
            </h1>
            <p className={styles.lead}>{t('home.hero.lead')}</p>
          </div>

          {/*
            Karl стоит на нижней границе hero, низ куртки срезан краем. Плашка
            «KI-Assistent» — требование прозрачности AI (DESIGN_SYSTEM §5.4):
            именная табличка у нижнего края фигуры, лицо она не закрывает.
            Больше поверх фигуры ничего не лежит (§10).
          */}
          <figure className={styles.karl}>
            <img
              className={styles.karlImage}
              src={EDITORIAL_PHOTOS.karl.src}
              width={EDITORIAL_PHOTOS.karl.width}
              height={EDITORIAL_PHOTOS.karl.height}
              alt={t('home.hero.karlImageAlt')}
              fetchPriority="high"
            />
            <figcaption className={styles.karlBadge}>
              <span className={styles.karlBadgeName}>{t('home.hero.badgeName')}</span>{' '}
              <span aria-hidden="true">·</span>{' '}
              <span className={styles.karlBadgeRole}>{t('home.hero.badgeRole')}</span>
            </figcaption>
          </figure>

          <div className={styles.heroPanel}>
            <ul className={styles.pillars} aria-label={t('home.pillars.label')}>
              {PILLARS.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <li key={pillar.key} className={styles.pillar}>
                    <Icon size={24} weight="regular" aria-hidden="true" />
                    <span className={styles.pillarTitle}>
                      {t(`home.pillars.${pillar.key}.title`)}
                    </span>
                    <span className={styles.pillarText}>
                      {t(`home.pillars.${pillar.key}.text`)}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/*
              Карточка запроса — главное действие продукта. Подпись поля уже
              называет textarea, поэтому у карточки своего имени нет: вторая
              ссылка на ту же строку сделала бы её именем двух элементов.
            */}
            <Card padding="lg" className={styles.promptCard}>
              <label className={styles.promptLabel} htmlFor="hero-prompt">
                <ChatCircleDotsIcon
                  size={20}
                  weight="regular"
                  aria-hidden="true"
                  className={styles.promptLabelIcon}
                />
                {t('home.promptLabel')}
              </label>
              <textarea
                ref={promptRef}
                id="hero-prompt"
                className={styles.promptInput}
                placeholder={t('home.promptPlaceholder')}
                rows={3}
                maxLength={limits.hero_prompt_max_length}
                value={prompt}
                aria-describedby="hero-prompt-hint"
                aria-invalid={showError}
                onChange={(event) => {
                  setPrompt(event.target.value);
                }}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') submit();
                }}
              />
              <p
                id="hero-prompt-hint"
                className={[styles.promptHint, showError && styles.promptHintError]
                  .filter(Boolean)
                  .join(' ')}
              >
                {/* Ошибка поля — текст и иконка, а не только цвет (DESIGN_SYSTEM §5.7). */}
                {showError ? (
                  <>
                    <WarningCircleIcon size={16} weight="regular" aria-hidden="true" />
                    {t('home.promptTooShort', { min: limits.hero_prompt_min_length })}
                  </>
                ) : (
                  t('home.promptHint', {
                    count: trimmed.length,
                    max: limits.hero_prompt_max_length,
                  })
                )}
              </p>

              <div className={styles.examples}>
                {EXAMPLE_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={styles.exampleChip}
                    onClick={() => {
                      // Пример подставляется в поле, но не отправляется автоматически.
                      setPrompt(t(`home.examples.${key}`));
                      setTouched(false);
                    }}
                  >
                    {t(`home.examples.${key}`)}
                  </button>
                ))}
              </div>

              <div className={styles.promptFooter}>
                <p className={styles.promptTip}>
                  <LightbulbIcon
                    size={20}
                    weight="regular"
                    aria-hidden="true"
                    className={styles.promptTipIcon}
                  />
                  {t('home.promptTip')}
                </p>

                <div className={styles.promptActions}>
                  {/*
                    Мост между главной и приложением — только вошедшему
                    пользователю: у него `/app` и есть список заказов.
                    Анонимному посетителю кнопка «мои прошлые подборы» вела бы
                    в форму первого запроса и обещала бы историю, которой нет.
                  */}
                  {session ? (
                    <ButtonLink to={ROUTES.app} size="lg" variant="secondary">
                      {t('home.previousSearches')}
                    </ButtonLink>
                  ) : null}
                  <Button size="lg" onClick={submit} iconEnd={<ArrowRightIcon size={18} />}>
                    {t('home.start')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </Container>
      </section>

      {/* Нижняя строка — под hero на белом, через всю ширину контейнера. */}
      <Container>
        <ul className={styles.assurances}>
          {ASSURANCES.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.key} className={styles.assurance}>
                <Icon size={24} weight="regular" aria-hidden="true" />
                <span>{t(`home.assurances.${item.key}`)}</span>
              </li>
            );
          })}
        </ul>
      </Container>

      {/* 2. Путь клиента — три этапа, две колонки на строку (DESIGN_SYSTEM §7). */}
      <section className={section.section} id="how-it-works">
        <Container>
          <Reveal className={styles.centerHead}>
            <h2 className={section.title} id="how-it-works-title">
              {t('home.steps.title')}
            </h2>
            <span className={styles.rule} aria-hidden="true" />
            <p className={styles.centerLead}>{t('home.steps.lead')}</p>
          </Reveal>

          {/* Порядок — часть смысла, поэтому список нумерованный. */}
          <ol className={styles.flow} aria-labelledby="how-it-works-title">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const price = stepPrice(step.key, config, locale, t);
              return (
                <Reveal as="li" key={step.key} index={index} className={styles.flowRow}>
                  <div className={styles.flowCopy}>
                    {/* На телефоне номер и заголовок встают в одну строку, а
                        иконка уходит: так этап не занимает целый экран. */}
                    <div className={styles.flowHead}>
                      <span className={`${styles.flowIndex} tabular`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <Icon
                        size={24}
                        weight="regular"
                        aria-hidden="true"
                        className={styles.flowIcon}
                      />
                      <h3 className={styles.flowTitle}>{t(`home.steps.${step.key}.title`)}</h3>
                    </div>
                    <p className={styles.flowText}>{t(`home.steps.${step.key}.text`)}</p>

                    <div className={styles.flowPrice}>
                      <TagIcon
                        size={18}
                        weight="regular"
                        aria-hidden="true"
                        className={styles.flowPriceIcon}
                      />
                      <div>
                        <p className={styles.flowPriceLines}>
                          <span className="visually-hidden">{t('home.steps.priceLabel')}: </span>
                          {price.lines.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </p>
                        {price.note ? <p className={styles.flowPriceNote}>{price.note}</p> : null}
                      </div>
                    </div>
                  </div>

                  <ul className={styles.flowDetails}>
                    {step.details.map((detail) => (
                      <li key={detail}>
                        <CheckIcon size={16} weight="bold" aria-hidden="true" />
                        <span>{t(`home.steps.${step.key}.details.${detail}`)}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </ol>

          {/*
            Действие в конце пути ведёт к тому же полю запроса в hero, а не в
            отдельный вход: разговор с Karl начинается одним способом. Красная
            кнопка на экране одна — «Mit Karl starten» (DESIGN_SYSTEM §5.1),
            поэтому здесь `outline-action`.
          */}
          <div className={styles.flowAction}>
            <Button
              size="lg"
              variant="outline-action"
              iconEnd={<ArrowRightIcon size={18} />}
              onClick={() => {
                promptRef.current?.focus();
              }}
            >
              {t('home.steps.action')}
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}
